import { useState, type ChangeEvent, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ENROLLMENT_STATUSES,
  type EnrollmentStatus,
  type QualificationCriterionDto,
  type QualificationDecision,
  type SaveChecklistAnswerRequest,
} from '@ctms/contracts';
import { AlertCircle, ClipboardCheck, X } from 'lucide-react';

import { getApiErrorMessage } from '../../../../api/client.js';
import { ENROLLMENT_STATUS_LABELS } from '../../enrollment-status-labels.js';
import { patientsApi } from '../../patients.api.js';
import { patientQueryKeys } from '../../patients.query-keys.js';

/** Sentinel value of the „— wolny tekst —" position of the answer select. */
const FREE_ANSWER = '__free__';

/** One checklist cell as recorded locally (gate 4: every change persists immediately). */
interface CellState {
  answer: string;
  met: boolean;
  comments: string | null;
}

/** A criterion row plus its effective cell (locally saved value wins over the server copy). */
interface CriterionRowView {
  criterion: QualificationCriterionDto;
  cell: CellState | null;
}

const fieldClass =
  'w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100 scheme-dark';

function normalizeComments(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

export interface QualificationDialogProps {
  /** The chart owning the participation (`patients.id`). */
  patientId: number;
  /** Presentation name of the patient (e.g. `Jan Kowalski`). */
  patientName: string;
  /** The participation being qualified (`trial_enrollments.id`). */
  enrollmentId: number;
  /** Trial presentation name — rendered in the header only when the caller has it. */
  trialLabel?: string | null;
  /**
   * `patients:write` of the signed-in user. A read-only user still BROWSES
   * the checklist (wireframe: „Przy uprawnieniu tylko do odczytu — checklista
   * jest przeglądana, decyzja niedostępna"); the UI hides the editing and the
   * decision, the backend enforces both (403).
   */
  canWrite: boolean;
  onClose: () => void;
}

interface CriterionRowProps {
  criterion: QualificationCriterionDto;
  cell: CellState | null;
  readOnly: boolean;
  onSave: (request: SaveChecklistAnswerRequest) => void;
}

/**
 * One CRITERION | VALUE | COMMENT row of the checklist. Editable while the
 * decision is available; after it (or once decided) the cell degrades to the
 * read-only text form `{answer} — spełnione/niespełnione` (REQ-PAT-023).
 */
function CriterionRow({ criterion, cell, readOnly, onSave }: CriterionRowProps): JSX.Element {
  const definedMatch =
    cell === null
      ? undefined
      : criterion.definedAnswers.find((item) => item.answer === cell.answer);
  const [freeSelected, setFreeSelected] = useState(false);
  const [freeText, setFreeText] = useState(
    cell !== null && definedMatch === undefined ? cell.answer : '',
  );
  const [metDraft, setMetDraft] = useState<boolean | null>(
    cell !== null && definedMatch === undefined ? cell.met : null,
  );
  const [commentsDraft, setCommentsDraft] = useState(cell?.comments ?? '');

  const showFree = freeSelected || (cell !== null && definedMatch === undefined);
  const selectValue = definedMatch ? String(definedMatch.id) : showFree ? FREE_ANSWER : '';

  if (readOnly) {
    return (
      <tr className="border-b border-slate-800/80">
        <td className="px-2 py-2 text-slate-100">{criterion.content}</td>
        <td className="px-2 py-2 text-slate-300">
          {cell === null ? '—' : `${cell.answer} — ${cell.met ? 'spełnione' : 'niespełnione'}`}
        </td>
        <td className="px-2 py-2 text-slate-400">
          {cell === null || cell.comments === null ? '—' : cell.comments}
        </td>
      </tr>
    );
  }

  // Gate 4: picking a defined answer persists the cell immediately.
  function handleSelectChange(event: ChangeEvent<HTMLSelectElement>): void {
    const value = event.target.value;
    if (value === FREE_ANSWER) {
      setFreeSelected(true);
      return;
    }
    const defined = criterion.definedAnswers.find((item) => String(item.id) === value);
    if (defined === undefined) {
      return;
    }
    setFreeSelected(false);
    onSave({
      criteriaId: criterion.id,
      answer: defined.answer,
      met: defined.met,
      comments: normalizeComments(commentsDraft),
    });
  }

  // Free text persists on blur / Enter, but only with the met mark chosen.
  function commitFreeText(): void {
    const text = freeText.trim();
    if (text.length === 0 || metDraft === null) {
      return;
    }
    if (cell !== null && cell.answer === text && cell.met === metDraft) {
      return;
    }
    onSave({
      criteriaId: criterion.id,
      answer: text,
      met: metDraft,
      comments: normalizeComments(commentsDraft),
    });
  }

  function handleMetChange(met: boolean): void {
    setMetDraft(met);
    const text = freeText.trim();
    if (text.length === 0) {
      return;
    }
    if (cell !== null && cell.answer === text && cell.met === met) {
      return;
    }
    onSave({
      criteriaId: criterion.id,
      answer: text,
      met,
      comments: normalizeComments(commentsDraft),
    });
  }

  // The comment hangs on a recorded answer — the field stays locked until one exists.
  function commitComments(): void {
    if (cell === null) {
      return;
    }
    const next = normalizeComments(commentsDraft);
    if (next === cell.comments) {
      return;
    }
    onSave({ criteriaId: criterion.id, answer: cell.answer, met: cell.met, comments: next });
  }

  return (
    <tr className="border-b border-slate-800/80">
      <td className="px-2 py-2 align-top text-slate-100">{criterion.content}</td>
      <td className="px-2 py-2 align-top">
        <select
          aria-label={`Odpowiedź — ${criterion.content}`}
          value={selectValue}
          onChange={handleSelectChange}
          className={fieldClass}
        >
          <option value="">— wybierz —</option>
          {criterion.definedAnswers.map((defined) => (
            <option key={defined.id} value={String(defined.id)}>
              {defined.answer}
            </option>
          ))}
          <option value={FREE_ANSWER}>— wolny tekst —</option>
        </select>
        {showFree ? (
          <div className="mt-2 space-y-2">
            <input
              type="text"
              aria-label={`Wolny tekst — ${criterion.content}`}
              value={freeText}
              placeholder="Wpisz odpowiedź"
              onChange={(event) => setFreeText(event.target.value)}
              onBlur={commitFreeText}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitFreeText();
                }
              }}
              className={fieldClass}
            />
            <div className="flex items-center gap-3 text-xs text-slate-300">
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name={`met-${criterion.id}`}
                  checked={metDraft === true}
                  onChange={() => handleMetChange(true)}
                />
                Spełnione
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name={`met-${criterion.id}`}
                  checked={metDraft === false}
                  onChange={() => handleMetChange(false)}
                />
                Niespełnione
              </label>
            </div>
          </div>
        ) : null}
      </td>
      <td className="px-2 py-2 align-top">
        <input
          type="text"
          aria-label={`Komentarz — ${criterion.content}`}
          value={commentsDraft}
          disabled={cell === null}
          placeholder={cell === null ? 'Najpierw zapisz odpowiedź' : 'Komentarz (opcjonalny)'}
          onChange={(event) => setCommentsDraft(event.target.value)}
          onBlur={commitComments}
          className={fieldClass}
        />
      </td>
    </tr>
  );
}

interface CriteriaSectionProps {
  title: string;
  rows: CriterionRowView[];
  readOnly: boolean;
  onSave: (request: SaveChecklistAnswerRequest) => void;
}

/** One of the two category sections (R3: exactly inclusion / exclusion). */
function CriteriaSection({
  title,
  rows,
  readOnly,
  onSave,
}: CriteriaSectionProps): JSX.Element | null {
  if (rows.length === 0) {
    return null;
  }
  return (
    <section aria-label={title}>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</h3>
      <table className="mt-2 w-full text-left text-xs">
        <thead>
          <tr className="border-b border-slate-800 text-slate-400">
            <th scope="col" className="px-2 py-2">
              KRYTERIUM
            </th>
            <th scope="col" className="px-2 py-2">
              WARTOŚĆ/ODPOWIEDŹ
            </th>
            <th scope="col" className="px-2 py-2">
              KOMENTARZ
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <CriterionRow
              key={row.criterion.id}
              criterion={row.criterion}
              cell={row.cell}
              readOnly={readOnly}
              onSave={onSave}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
}

/**
 * Qualification checklist and decision of one participation (issue #30,
 * REQ-PAT-018…023). Gate 4: every cell persists the moment it changes; the
 * summary line is INFORMATIONAL and never blocks the decision (gate 3); R4:
 * the decision submits the current checklist state and the backend stores
 * both atomically. Opened from the chart participation bar (element 6).
 */
export function QualificationDialog({
  patientId,
  patientName,
  enrollmentId,
  trialLabel = null,
  canWrite,
  onClose,
}: QualificationDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Cells saved in this dialog session — they win over the server copy until refetch.
  const [cells, setCells] = useState<Record<number, CellState>>({});
  const [decided, setDecided] = useState(false);
  const [decidedStatus, setDecidedStatus] = useState<EnrollmentStatus | null>(null);
  const [pendingDecision, setPendingDecision] = useState<QualificationDecision | null>(null);

  const checklistQuery = useQuery({
    queryKey: patientQueryKeys.qualification(patientId, enrollmentId),
    queryFn: () => patientsApi.getQualificationChecklist(patientId, enrollmentId),
  });

  const saveMutation = useMutation({
    mutationFn: (request: SaveChecklistAnswerRequest) =>
      patientsApi.saveQualificationAnswer(patientId, enrollmentId, request),
  });

  const decideMutation = useMutation({
    mutationFn: (variables: {
      decision: QualificationDecision;
      answers: SaveChecklistAnswerRequest[];
    }) =>
      patientsApi.decideQualification(patientId, enrollmentId, {
        decision: variables.decision,
        answers: variables.answers,
      }),
  });

  const data = checklistQuery.data;

  function effectiveCell(criterion: QualificationCriterionDto): CellState | null {
    const edited = cells[criterion.id];
    if (edited !== undefined) {
      return edited;
    }
    if (criterion.savedAnswer === null) {
      return null;
    }
    return {
      answer: criterion.savedAnswer.answer,
      met: criterion.savedAnswer.met,
      comments: criterion.savedAnswer.comments,
    };
  }

  const criteria = data?.criteria ?? [];
  const inclusionRows: CriterionRowView[] = criteria
    .filter((criterion) => criterion.category === 'inclusion')
    .map((criterion) => ({ criterion, cell: effectiveCell(criterion) }));
  const exclusionRows: CriterionRowView[] = criteria
    .filter((criterion) => criterion.category === 'exclusion')
    .map((criterion) => ({ criterion, cell: effectiveCell(criterion) }));
  const inclusionMet = inclusionRows.filter((row) => row.cell !== null && row.cell.met).length;
  const exclusionActive = exclusionRows.filter((row) => row.cell !== null && !row.cell.met).length;
  const readOnly =
    !canWrite || decided || data === undefined || !data.decisionAvailable;

  function handleSaveCell(request: SaveChecklistAnswerRequest): void {
    saveMutation.mutate(request, {
      onSuccess: (response) => {
        setErrorMessage(null);
        setCells((previous) => ({
          ...previous,
          [request.criteriaId]: {
            answer: response.answer.answer,
            met: response.answer.met,
            comments: response.answer.comments,
          },
        }));
        // Gate 4 seam: this local `cells` copy dies with the dialog — sync the
        // cached checklist so a reopen within staleTime restores saved answers.
        void queryClient.invalidateQueries({
          queryKey: patientQueryKeys.qualification(patientId, enrollmentId),
        });
      },
      onError: async (error: unknown) => {
        setErrorMessage(await getApiErrorMessage(error, 'Nie udało się zapisać odpowiedzi.'));
      },
    });
  }

  /** The CURRENT checklist state submitted atomically with the decision (R4). */
  function buildAnswers(): SaveChecklistAnswerRequest[] {
    const answers: SaveChecklistAnswerRequest[] = [];
    for (const row of [...inclusionRows, ...exclusionRows]) {
      if (row.cell !== null) {
        answers.push({
          criteriaId: row.criterion.id,
          answer: row.cell.answer,
          met: row.cell.met,
          comments: row.cell.comments,
        });
      }
    }
    return answers.sort((left, right) => left.criteriaId - right.criteriaId);
  }

  function handleConfirmDecision(): void {
    if (pendingDecision === null || data === undefined) {
      return;
    }
    decideMutation.mutate(
      { decision: pendingDecision, answers: buildAnswers() },
      {
        onSuccess: async (response) => {
          setErrorMessage(null);
          setPendingDecision(null);
          setDecided(true);
          setDecidedStatus(response.enrollment.status);
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: patientQueryKeys.enrollments(patientId),
            }),
            queryClient.invalidateQueries({
              queryKey: patientQueryKeys.qualification(patientId, enrollmentId),
            }),
            // The #29 participation panel shows this decision as a history row
            // and re-derives the matrix from the new status — refresh it too.
            queryClient.invalidateQueries({
              queryKey: patientQueryKeys.decisions(patientId, enrollmentId),
            }),
          ]);
        },
        onError: async (error: unknown) => {
          setPendingDecision(null);
          setErrorMessage(await getApiErrorMessage(error, 'Nie udało się zapisać decyzji.'));
        },
      },
    );
  }

  const decisionDescription =
    pendingDecision === 'positive'
      ? `Status udziału zmieni się na ${ENROLLMENT_STATUS_LABELS[ENROLLMENT_STATUSES.SCREENED]}.`
      : `Status udziału zmieni się na ${ENROLLMENT_STATUS_LABELS[ENROLLMENT_STATUSES.SCREENING_FAILURE]}.`;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="qualification-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="flex max-h-full w-full max-w-3xl flex-col rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2
              id="qualification-dialog-title"
              className="flex items-center gap-2 text-base font-semibold text-slate-100"
            >
              <ClipboardCheck className="h-4 w-4 text-cyan-400" />
              Kwalifikacja — kryteria
            </h2>
            <p className="mt-1 text-xs text-slate-400">
              Pacjent: <span className="font-medium text-slate-200">{patientName}</span>
              {trialLabel !== null && trialLabel.length > 0 ? (
                <>
                  {' '}
                  · Badanie: <span className="font-medium text-slate-200">{trialLabel}</span>
                </>
              ) : null}
            </p>
            {data !== undefined ? (
              <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-400">
                Udział:
                <span className="rounded-full border border-slate-700 bg-slate-950 px-2 py-0.5 text-[11px] font-medium text-cyan-300">
                  {ENROLLMENT_STATUS_LABELS[decidedStatus ?? data.status]}
                </span>
                · Kryteria: {data.summary.criteriaTotal}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij"
            className="rounded-md p-1.5 text-slate-400 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {errorMessage !== null ? (
          <div
            role="alert"
            className="mt-4 flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-xs text-rose-300"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{errorMessage}</span>
          </div>
        ) : null}

        {checklistQuery.isLoading ? (
          <p role="status" className="mt-4 text-xs text-slate-400">
            Ładowanie checklisty
          </p>
        ) : checklistQuery.isError ? (
          <div
            role="alert"
            className="mt-4 rounded-md border border-rose-800/70 bg-rose-950/40 p-3"
          >
            <p className="text-xs text-rose-200">Nie udało się wczytać kryteriów kwalifikacji.</p>
            <button
              type="button"
              onClick={() => {
                void checklistQuery.refetch();
              }}
              className="mt-2 rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-200"
            >
              Spróbuj ponownie
            </button>
          </div>
        ) : data !== undefined ? (
          <>
            <p className="mt-4 text-xs text-slate-300">
              {inclusionMet}/{inclusionRows.length} włączenia spełnione, {exclusionActive}{' '}
              wyłączeń aktywnych.
            </p>
            {readOnly ? (
              <p className="mt-2 text-xs text-slate-400">
                Odpowiedzi i decyzja są zablokowane do edycji.
              </p>
            ) : null}
            <div className="mt-4 space-y-5 overflow-y-auto">
              {data.criteria.length === 0 ? (
                <p className="text-xs text-slate-400">Brak kryteriów dla tego badania.</p>
              ) : (
                <>
                  <CriteriaSection
                    title="Kryteria włączenia"
                    rows={inclusionRows}
                    readOnly={readOnly}
                    onSave={handleSaveCell}
                  />
                  <CriteriaSection
                    title="Kryteria wyłączenia"
                    rows={exclusionRows}
                    readOnly={readOnly}
                    onSave={handleSaveCell}
                  />
                </>
              )}
            </div>
          </>
        ) : null}

        <div className="mt-5 flex items-center justify-between gap-2 border-t border-slate-800 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-700 px-3.5 py-1.5 text-xs font-medium text-slate-300 hover:bg-slate-800"
          >
            Zamknij
          </button>
          {data !== undefined && !readOnly ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPendingDecision('negative')}
                disabled={decideMutation.isPending}
                className="rounded-md border border-rose-700/70 bg-rose-950/40 px-3.5 py-1.5 text-xs font-medium text-rose-300 hover:bg-rose-900/50"
              >
                Screening failure
              </button>
              <button
                type="button"
                onClick={() => setPendingDecision('positive')}
                disabled={decideMutation.isPending}
                className="rounded-md border border-emerald-700/70 bg-emerald-950/40 px-3.5 py-1.5 text-xs font-medium text-emerald-300 hover:bg-emerald-900/50"
              >
                Zakwalifikuj
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {pendingDecision !== null ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="qualification-decision-title"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 p-4"
        >
          <div className="w-full max-w-sm rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
            <h3 id="qualification-decision-title" className="text-sm font-semibold text-slate-100">
              Potwierdź decyzję
            </h3>
            <p className="mt-2 text-xs text-slate-300">
              Decyzja jest nieodwracalna w tym dialogu.
            </p>
            <p className="mt-2 text-xs text-slate-400">{decisionDescription}</p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingDecision(null)}
                className="rounded-md border border-slate-700 px-3.5 py-1.5 text-xs font-medium text-slate-300 hover:bg-slate-800"
              >
                Wróć do checklisty
              </button>
              <button
                type="button"
                onClick={handleConfirmDecision}
                disabled={decideMutation.isPending}
                className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500"
              >
                Potwierdź i zapisz
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
